import { computed, ref, type ComputedRef } from 'vue'
import type { ItemRow } from '~~/shared/items/itemRow'
import type { FolderInfo } from '~~/shared/items/folderInfo'
import { mapWith, pruneIdLists, setWith, setWithout, type EntityCache } from '~/stores/entityCache'
import type { ApiFetch, ListingParams, LoadState } from '~/composables/documents/browser'

export interface FolderTreeDeps {
  apiFetch: ApiFetch
  cache: EntityCache<ItemRow>
  listingParams: ComputedRef<ListingParams>
  trackPending: (rows: ItemRow[]) => void
}

/**
 * The folder list and the expanded-folder tree: which folders exist, which are open, and each open
 * folder's children (MAR-948 — split out of the documents store, whose facade still returns all of
 * it under the same names).
 */
export function useFolderTree({ apiFetch, cache, listingParams, trackPending }: FolderTreeDeps) {
  const folders = ref<FolderInfo[]>([])
  /** Whether `/api/folders` ANSWERED (AlexAmin/marcus#469) — see `LoadState`. */
  const foldersState = ref<LoadState>('idle')
  const foldersFailed = computed(() => foldersState.value === 'failed')
  const childIds = ref<Map<string, string[]>>(new Map())
  const expandedFolders = ref<Set<string>>(new Set())
  const loadingChildren = ref<Set<string>>(new Set())
  /** Keyed by folder: one broken expansion must not accuse the others (AlexAmin/marcus#469). */
  const failedChildren = ref<Set<string>>(new Set())

  function markChildrenFailed(folderId: string, failed: boolean) {
    failedChildren.value = failed ? setWith(failedChildren.value, folderId) : setWithout(failedChildren.value, folderId)
  }

  function getChildren(folderId: string): ItemRow[] {
    return cache.resolve(childIds.value.get(folderId) ?? [])
  }

  function isChildrenLoading(id: string): boolean {
    return loadingChildren.value.has(id)
  }

  /**
   * Whether THIS folder's last children request failed (AlexAmin/marcus#469).
   *
   * Per folder rather than one flag, because `BrowserItemList` renders every expanded folder in the
   * same tree: a sibling that loaded fine is still telling the truth, and one broken expansion must
   * not put a notice under all of them.
   */
  function isChildrenFailed(id: string): boolean {
    return failedChildren.value.has(id)
  }

  function isExpanded(id: string): boolean {
    return expandedFolders.value.has(id)
  }

  async function fetchFolders() {
    foldersState.value = 'loading'
    try {
      folders.value = await apiFetch<FolderInfo[]>('/api/folders')
      foldersState.value = 'ready'
    } catch (err) {
      // "tracked by useApi" was false here too (AlexAmin/marcus#469). This one has no empty state of
      // its own — `folders` feeds the breadcrumb and the "Move to" submenu, both of which simply
      // render one fewer option — so the visible damage lands in `fetchChildren`, which resolves a
      // folder's path through this list and treats a miss as "that folder does not exist": it drops
      // the folder out of `expandedFolders`, so a chevron the user just clicked silently
      // un-expanded and NOTHING said why. That branch now asks `foldersFailed` first.
      foldersState.value = 'failed'
      console.error('[documents] /api/folders failed', err)
    }
  }

  async function fetchChildren(folderId: string) {
    // If the folder list hasn't loaded yet, fetch it first so a quick chevron click still works.
    if (folders.value.length === 0) await fetchFolders()
    const folder = folders.value.find(f => f.id === folderId)
    if (!folder) {
      // A FOLDER LIST THAT FAILED IS NOT A FOLDER THAT DOES NOT EXIST (AlexAmin/marcus#469).
      // Collapsing here is right when the list ANSWERED and the folder genuinely is not in it (it
      // was deleted elsewhere). With a failed `/api/folders` the same branch un-expanded the chevron
      // the user had just clicked and said nothing, so the click read as a dead control.
      if (foldersFailed.value) {
        markChildrenFailed(folderId, true)
        return
      }
      expandedFolders.value = setWithout(expandedFolders.value, folderId)
      return
    }
    loadingChildren.value = setWith(loadingChildren.value, folderId)
    try {
      // An expanded folder is part of the listing the column header describes, so it takes the
      // same lens (MAR-957) — without it "PDF only" listed an expanded folder's DOCX files.
      const rows = await apiFetch<ItemRow[]>('/api/items', {
        params: { path: folder.full_path, ...listingParams.value },
      })
      cache.upsert(rows)
      childIds.value = mapWith(childIds.value, folderId, rows.map(r => r.id))
      markChildrenFailed(folderId, false)
      trackPending(rows)
    } catch (err) {
      // THE SHARPEST OF THE SEVEN (AlexAmin/marcus#469). `BrowserItemList` shows a spinner while
      // children load *specifically so it never flashes "Carpeta vacía"* — a deliberate choice
      // recorded in the feature doc. A failed fetch walked straight past it: `childIds` got no
      // entry, the spinner cleared in the `finally` below, and the row settled on
      // `browser.emptyFolder`, which is the exact sentence the spinner exists to prevent.
      markChildrenFailed(folderId, true)
      console.error(`[documents] /api/items failed for folder ${folderId}`, err)
    } finally {
      loadingChildren.value = setWithout(loadingChildren.value, folderId)
    }
  }

  async function expandFolder(item: ItemRow) {
    if (item.type !== 'folder' || expandedFolders.value.has(item.id)) return
    expandedFolders.value = setWith(expandedFolders.value, item.id)
    if (!childIds.value.has(item.id)) await fetchChildren(item.id)
  }

  function collapseFolder(id: string) {
    expandedFolders.value = setWithout(expandedFolders.value, id)
  }

  async function toggleExpand(item: ItemRow) {
    if (expandedFolders.value.has(item.id)) collapseFolder(item.id)
    else await expandFolder(item)
  }

  /** Re-read every open folder's children — after a refresh, or when the lens changes. */
  function refetchExpanded(): Promise<void[]> {
    return Promise.all([...expandedFolders.value].map(id => fetchChildren(id)))
  }

  /** Drop removed documents from every open folder. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    childIds.value = pruneIdLists(childIds.value, gone)
  }

  return {
    folders, foldersState, foldersFailed, expandedFolders,
    getChildren, isChildrenLoading, isChildrenFailed, isExpanded,
    fetchFolders, fetchChildren, expandFolder, collapseFolder, toggleExpand, refetchExpanded, prune,
  }
}
